import React, { useState, useEffect } from 'react';
import { Plus, Trash2, ExternalLink } from 'lucide-react';

const ProductLinksEditor = ({ productLinks = [], onChange }) => {
  const [links, setLinks] = useState(productLinks);

  // Sync local state when productLinks prop changes
  useEffect(() => {
    setLinks(productLinks);
  }, [productLinks]);

  const addLink = () => {
    const newLinks = [...links, { title: '', url: '', description: '' }];
    setLinks(newLinks);
    onChange(newLinks);
  };

  const removeLink = (index) => {
    const newLinks = links.filter((_, i) => i !== index);
    setLinks(newLinks);
    onChange(newLinks);
  };

  const updateLink = (index, field, value) => {
    const newLinks = links.map((link, i) => 
      i === index ? { ...link, [field]: value } : link
    );
    setLinks(newLinks);
    onChange(newLinks);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Product Links
        </label>
        <button
          type="button"
          onClick={addLink}
          className="inline-flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Link
        </button>
      </div>

      <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded">
        <ExternalLink className="w-3 h-3 inline mr-1" />
        These links will be available to the AI and can be included in responses as clickable links.
      </div>

      {links.length === 0 ? (
        <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <ExternalLink className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p>No product links added yet.</p>
          <p className="text-xs">Click "Add Link" to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {links.map((link, index) => (
            <div key={index} className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-start justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">Link #{index + 1}</span>
                <button
                  type="button"
                  onClick={() => removeLink(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Link Title *
                  </label>
                  <input
                    type="text"
                    value={link.title}
                    onChange={(e) => updateLink(index, 'title', e.target.value)}
                    placeholder="e.g., Premium Ceiling Installation"
                    className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    URL *
                  </label>
                  <input
                    type="url"
                    value={link.url}
                    onChange={(e) => updateLink(index, 'url', e.target.value)}
                    placeholder="https://yourwebsite.com/product-page"
                    className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Description (optional)
                  </label>
                  <input
                    type="text"
                    value={link.description}
                    onChange={(e) => updateLink(index, 'description', e.target.value)}
                    placeholder="Brief description of this product/page"
                    className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {link.url && (
                  <div className="text-xs text-gray-500">
                    Preview: <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {link.title || 'Untitled Link'}
                    </a>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="text-xs text-gray-500 bg-yellow-50 p-2 rounded border border-yellow-200">
        <strong>How it works:</strong> When users ask questions related to this context, 
        the AI can include these links in responses as clickable HTML links that open in new tabs.
      </div>
    </div>
  );
};

export default ProductLinksEditor;